import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Trash2, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Button } from "@/components/ui/button";

export default function RecentSearches() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    const fetchHistory = async () => {
      setLoading(true);
      setError(null);
      try {
        const parsed = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
        const list = Array.isArray(parsed) ? parsed : [];
        if (list.length === 0) {
          if (live) {
            setProperties([]);
            setLoading(false);
          }
          return;
        }

        // Fetch properties concurrently
        let failedRequests = 0;
        const promises = list.map(id =>
          api.get(`/properties/${id}`)
            .then(res => res.data)
            .catch((err) => {
              if (err?.response?.status === 404) {
                try {
                  const current = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
                  const updated = current.filter(x => x !== id);
                  localStorage.setItem("recently_viewed_properties", JSON.stringify(updated));
                } catch {
                  // A corrupt history entry should not keep breaking this page.
                  localStorage.removeItem("recently_viewed_properties");
                }
              } else {
                failedRequests += 1;
              }
              return null;
            })
        );
        const results = await Promise.all(promises);
        if (live) {
          // Filter out failed or deleted properties
          const validProperties = results.filter(Boolean);
          setProperties(validProperties);
          if (failedRequests > 0) {
            setError(validProperties.length > 0
              ? `${failedRequests} recent listing${failedRequests === 1 ? " is" : "s are"} temporarily unavailable.`
              : "Recently viewed properties could not be loaded. Please try again.");
          }
        }
      } catch (err) {
        console.error("Error loading recent searches:", err);
        if (live) {
          localStorage.removeItem("recently_viewed_properties");
          setProperties([]);
          setError("Failed to load your recently searched properties.");
        }
      } finally {
        if (live) {
          setLoading(false);
        }
      }
    };

    fetchHistory();
    return () => { live = false; };
  }, [reloadKey]);

  const handleRemoveRecent = (propertyId) => {
    try {
      const list = JSON.parse(localStorage.getItem("recently_viewed_properties") || "[]");
      const updated = list.filter((id) => id !== propertyId);
      localStorage.setItem("recently_viewed_properties", JSON.stringify(updated));
      setProperties(prev => prev.filter(p => (p.property_id || p.id) !== propertyId));
    } catch (e) {
      console.error("Error removing property from history:", e);
    }
  };

  const handleClearHistory = () => {
    try {
      localStorage.removeItem("recently_viewed_properties");
      setProperties([]);
    } catch (e) {
      console.error("Error clearing history:", e);
    }
  };

  return (
    <div className="bg-slate-50/50 min-h-screen py-12" data-testid="recent-searches-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Navigation Breadcrumb */}
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold text-[#0078DB] hover:text-[#005cab] mb-6 transition">
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        {/* Section Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 border-l-4 border-[#0078DB] pl-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900">
              Recently Searched Properties
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1">
              Here are all the properties you've searched or viewed recently.
            </p>
          </div>
          {properties.length > 0 && (
            <Button
              onClick={handleClearHistory}
              variant="destructive"
              size="sm"
              className="font-bold gap-2 text-xs shrink-0 self-start sm:self-auto bg-red-600 hover:bg-red-700 text-white"
              data-testid="clear-history-btn"
            >
              <Trash2 className="w-4 h-4" /> Clear History
            </Button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-96 rounded-2xl shimmer border border-slate-200" />
            ))}
          </div>
        )}

        {/* Error State */}
        {!loading && error && properties.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-red-100 shadow-sm">
            <p className="text-red-500 font-bold mb-4">{error}</p>
            <Button onClick={() => setReloadKey((value) => value + 1)} className="bg-primary hover:bg-primary/95 text-white">
              Try Again
            </Button>
          </div>
        )}

        {!loading && error && properties.length > 0 && (
          <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 sm:flex-row sm:items-center sm:justify-between" role="status">
            <span>{error}</span>
            <Button type="button" size="sm" variant="outline" onClick={() => setReloadKey((value) => value + 1)}>Retry missing listings</Button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && properties.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200 shadow-sm max-w-xl mx-auto">
            <p className="text-slate-600 font-medium mb-2">
              You have not viewed or searched any properties yet.
            </p>
            <p className="text-slate-400 text-xs mb-6 max-w-xs mx-auto">
              Start browsing listings on ShreeHomes to populate your history.
            </p>
            <Button asChild className="bg-[#0078DB] hover:bg-[#005cab] text-white font-bold px-8"><Link to="/properties">Explore Properties</Link></Button>
          </div>
        )}

        {/* Grid of Results */}
        {!loading && properties.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((p) => (
              <div key={p.property_id || p.id} className="relative group">
                <PropertyCard 
                  property={p} 
                  onRemoveRecent={(id, e) => handleRemoveRecent(id, e)} 
                />
                <button
                  onClick={() => handleRemoveRecent(p.property_id || p.id)}
                  className="absolute top-3 right-16 z-20 w-8 h-8 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition border border-white/10"
                  title="Remove from history"
                  data-testid={`remove-recent-btn-${p.property_id || p.id}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
